import React from "react";
import Typography from "@mui/material/Typography";
import CopyClipboardComponent from "../../../../CopyClipboardComponent";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CancelIcon from "@mui/icons-material/Cancel";
import { LoadingButton } from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import { requestApi } from "../../../../../../helpers/RequestApi";
import { POST } from "../../../../../../utils/MethodUtils";
import { EDIT_USER_URL } from "../../../../../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import { useAppSelector } from "../../../../../../app/hooks";
import { RootState } from "../../../../../../app/store";
import { TextField } from "@mui/material";

interface State {
  field: string;
  ctNum: string | undefined;
  values: any;
  setValues: Function;
  withoutSpace?: boolean;
}

const EditUserFieldComponent: React.FC<State> = React.memo(
  ({ field, ctNum, values, setValues, withoutSpace }) => {
    const { t } = useTranslation();
    const [edit, setEdit] = React.useState(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const token = useAppSelector((state: RootState) => state.globalState.token);
    const [fieldValue, setFieldValue] = React.useState<string>(
      values[field].value
    );

    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        let v = event.target.value;
        if (withoutSpace) {
          v = v.replace(" ", "");
        }
        setFieldValue(v);
      },
      [withoutSpace]
    );

    const save = React.useCallback(async () => {
      const response = await requestApi({
        method: POST,
        path: EDIT_USER_URL,
        allowError: true,
        token: token,
        body: {
          userIdentifier: ctNum,
          [field]: fieldValue,
        },
      });
      if (response.statusCode === 200) {
        setValues((v: any) => {
          return {
            ...v,
            [field]: { ...v[field], value: response.content[field] },
          };
        });
        setTimeout(() => {
          setEdit(false);
        });
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        toastr.error(t("word.error"), t("error.tryAgain"));
      }
      setLoading(false);
    }, [ctNum, field, fieldValue, setValues, t, token]);

    React.useEffect(() => {
      if (!edit) {
        setFieldValue(values[field].value);
      }
    }, [edit, values[field]?.value]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <>
        {!edit ? (
          <Typography>
            {t("field." + field) + ": "}
            <CopyClipboardComponent
              className="RobotoMono"
              component="span"
              text={values[field].value}
            />
            <IconButton
              onClick={() => {
                setEdit(true);
              }}
            >
              <EditIcon />
            </IconButton>
          </Typography>
        ) : (
          <Typography>
            <TextField
              fullWidth={true}
              autoComplete="off"
              sx={{ width: "100%", marginTop: 1 }}
              required
              type="text"
              value={fieldValue}
              onChange={handleChange}
              label={t("field." + field)}
            />
            <>
              <Tooltip title={t("word.cancel")}>
                <IconButton
                  onClick={() => {
                    setEdit(false);
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("word.save")} placement="right">
                <LoadingButton
                  variant="text"
                  sx={{
                    borderRadius: "50%",
                    minWidth: "auto",
                    padding: "12px",
                  }}
                  loading={loading}
                  onClick={save}
                >
                  <SaveIcon />
                </LoadingButton>
              </Tooltip>
            </>
          </Typography>
        )}
      </>
    );
  }
);

export default EditUserFieldComponent;
